import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FiCheck, FiX } from 'react-icons/fi';

const PricingPage = () => {
  const tiers = [
    {
      name: 'Starter',
      price: 49,
      description: 'For single-lot dealers getting started with digital inventory management.',
      features: [
        { text: 'Dealer Management System', included: true },
        { text: 'Up to 50 listings', included: true },
        { text: 'Basic CRM & lead capture', included: true },
        { text: 'Reporting dashboard', included: true },
        { text: 'Mobile-responsive design', included: true },
        { text: 'Marketplace listing', included: true },
        { text: '3D Shed Configurator', included: false },
        { text: 'RTO contract integration', included: false },
        { text: 'API access', included: false },
        { text: 'White-label website', included: false },
      ],
      cta: 'Start Free Trial',
      popular: false,
    },
    {
      name: 'Growth',
      price: 149,
      description: 'For growing dealers who need the full toolkit to compete and win.',
      features: [
        { text: 'Dealer Management System', included: true },
        { text: 'Unlimited listings', included: true },
        { text: 'Full CRM & lead automation', included: true },
        { text: 'Advanced reporting & analytics', included: true },
        { text: 'Mobile-responsive design', included: true },
        { text: 'Priority marketplace listing', included: true },
        { text: '3D Shed Configurator', included: true },
        { text: 'RTO contract integration', included: true },
        { text: 'API access', included: false },
        { text: 'White-label website', included: false },
      ],
      cta: 'Start Free Trial',
      popular: true,
    },
    {
      name: 'Enterprise',
      price: 349,
      description: 'For manufacturers and multi-location operations needing full control.',
      features: [
        { text: 'Dealer Management System', included: true },
        { text: 'Unlimited listings', included: true },
        { text: 'Full CRM & lead automation', included: true },
        { text: 'Advanced reporting & analytics', included: true },
        { text: 'Mobile-responsive design', included: true },
        { text: 'Featured marketplace listing', included: true },
        { text: '3D Shed Configurator', included: true },
        { text: 'RTO contract integration', included: true },
        { text: 'Full API access', included: true },
        { text: 'White-label website', included: true },
      ],
      cta: 'Contact Sales',
      popular: false,
    },
  ];

  const comparisons = [
    { feature: 'Monthly cost (200 sheds)', us: '$49–$149', them: '$1,900–$3,900 in credits' },
    { feature: 'Listings model', us: 'Unlimited (Growth+)', them: 'Pay-per-listing credits' },
    { feature: '3D Configurator', us: 'Included (Growth+)', them: 'Does not exist' },
    { feature: 'CRM / Lead Management', us: 'Included', them: 'Does not exist' },
    { feature: 'Inventory Management', us: 'Full DMS', them: 'Listing only' },
    { feature: 'RTO Integration', us: 'Included (Growth+)', them: '$2,499/mo tier only' },
    { feature: 'Mobile App', us: 'Mobile-first web app', them: '2.3★ app, abandoned 2021' },
    { feature: 'Reporting Dashboard', us: 'Included', them: 'Does not exist' },
    { feature: 'API Access', us: '$349/mo', them: '$2,499/mo' },
    { feature: 'Payment Processing', us: 'Stripe integrated', them: 'Not available' },
  ];

  return (
    <>
      <Helmet>
        <title>Pricing Plans for Shed Dealers | ShedX Platform</title>
        <meta name="description" content="Simple pricing for shed dealers. Full DMS, CRM, 3D configurator and marketplace listing starting at $49/mo. No per-listing credits." />
      </Helmet>

      <div className="min-h-screen bg-gray-50 pt-24 pb-16">
        {/* Header */}
        <div className="text-center px-4 mb-12">
          <h1 className="text-3xl font-semibold text-gray-900 mb-3">Simple, transparent pricing</h1>
          <p className="text-gray-500 max-w-xl mx-auto">
            No per-listing credits. No hidden fees. Every plan includes marketplace visibility and a full dealer management system.
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="container mx-auto px-4 mb-20">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-gray-200 border border-gray-200 max-w-5xl mx-auto">
            {tiers.map((tier) => (
              <div key={tier.name} className={`bg-white p-8 ${tier.popular ? 'ring-2 ring-gray-900 ring-inset' : ''}`}>
                {tier.popular && (
                  <span className="text-[11px] uppercase tracking-wider font-medium text-gray-900 bg-gray-100 px-2 py-0.5 mb-4 inline-block">
                    Most Popular
                  </span>
                )}
                <h2 className="text-xl font-semibold text-gray-900">{tier.name}</h2>
                <div className="mt-3 mb-4">
                  <span className="text-4xl font-semibold text-gray-900">${tier.price}</span>
                  <span className="text-gray-400 text-sm">/month</span>
                </div>
                <p className="text-sm text-gray-500 mb-6">{tier.description}</p>

                <Link
                  to="/contact"
                  className={`block w-full text-center text-sm font-medium py-3 transition-colors mb-8 ${
                    tier.popular
                      ? 'bg-gray-900 text-white hover:bg-gray-800'
                      : 'border border-gray-300 text-gray-700 hover:border-gray-400'
                  }`}
                >
                  {tier.cta}
                </Link>

                <ul className="space-y-3">
                  {tier.features.map((f) => (
                    <li key={f.text} className="flex items-start gap-2 text-sm">
                      {f.included ? (
                        <FiCheck className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      ) : (
                        <FiX className="w-4 h-4 text-gray-300 flex-shrink-0 mt-0.5" />
                      )}
                      <span className={f.included ? 'text-gray-700' : 'text-gray-400'}>{f.text}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Comparison Table */}
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-2xl font-semibold text-gray-900 text-center mb-2">How we stack up</h2>
          <p className="text-sm text-gray-500 text-center mb-8">Feature-by-feature comparison with the leading shed marketplace.</p>

          <div className="bg-white border border-gray-200 overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="text-left p-4 text-xs text-gray-400 uppercase tracking-wider font-medium">Feature</th>
                  <th className="text-left p-4 text-xs text-gray-400 uppercase tracking-wider font-medium">ShedX</th>
                  <th className="text-left p-4 text-xs text-gray-400 uppercase tracking-wider font-medium">ShedHub</th>
                </tr>
              </thead>
              <tbody>
                {comparisons.map((row, i) => (
                  <tr key={row.feature} className={`border-b border-gray-100 ${i % 2 === 0 ? '' : 'bg-gray-50/50'}`}>
                    <td className="p-4 text-sm text-gray-700 font-medium">{row.feature}</td>
                    <td className="p-4 text-sm text-gray-900 font-medium">{row.us}</td>
                    <td className="p-4 text-sm text-gray-400">{row.them}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-16 px-4">
          <h3 className="text-xl font-semibold text-gray-900 mb-3">Ready to modernize your shed business?</h3>
          <p className="text-sm text-gray-500 mb-6">Start your free 30-day trial. No credit card required.</p>
          <Link to="/contact" className="inline-block bg-gray-900 text-white text-sm font-medium px-8 py-3.5 hover:bg-gray-800 transition-colors">
            Get Started Free
          </Link>
        </div>
      </div>
    </>
  );
};

export default PricingPage;
